import { useMemo } from "react";
import { Affiliate, User } from "types";
import { useCollection } from "@/hooks/useCollection";
import { isAffiliate } from "@/models/User";

interface PartnerFilter {
    partner: ID;
}

interface UseAffiliatePurchaseScopeResult {
    isAffiliateScopeReady: boolean;
    allowedPartnerIdsSet: Set<ID>;
    partnerFilter?: PartnerFilter;
}

export const useAffiliatePurchaseScope = (user: User): UseAffiliatePurchaseScopeResult => {
    const isUserAffiliate = isAffiliate(user);
    const userId = user?.id;

    const shouldFetchAffiliate = isUserAffiliate && !!userId;
    const { data: affiliateData, isLoading: isLoadingAffiliate } = useCollection<Affiliate>(
        "affiliates",
        { user: userId, limit: 1 },
        { enabled: shouldFetchAffiliate },
    );
    const loggedInAffiliate = affiliateData[0];

    // Affiliate purchases are scoped by the `partner` field (the affiliate whose code was used), so
    // the affiliate sees every booking their code was applied to regardless of who created it
    // (checkout, back-office, impersonation, etc.). The backend resolves child affiliates when we
    // filter by the parent's id, so the list query only needs the logged-in affiliate's own id.
    const partnerFilter = useMemo(() => {
        if (!isUserAffiliate || !userId) {
            return undefined;
        }

        // Wait for the affiliate record to load before building the filter
        if (shouldFetchAffiliate && isLoadingAffiliate) {
            return undefined;
        }

        return loggedInAffiliate?.id ? { partner: loggedInAffiliate.id } : undefined;
    }, [isLoadingAffiliate, isUserAffiliate, loggedInAffiliate, shouldFetchAffiliate, userId]);

    // The list query is filtered server-side, but purchases fetched by id (the selected purchase
    // and the contact-timeline purchase) bypass that filter, so callers re-check them against this
    // set. It holds the parent affiliate id plus its child affiliate ids, mirroring the partner
    // scope the backend resolves for the list.
    const allowedPartnerIdsSet = useMemo(() => {
        if (!partnerFilter) {
            return new Set<ID>();
        }

        return new Set<ID>([partnerFilter.partner, ...(loggedInAffiliate?.children ?? [])]);
    }, [partnerFilter, loggedInAffiliate]);

    return {
        isAffiliateScopeReady: !isUserAffiliate || !!partnerFilter,
        allowedPartnerIdsSet,
        partnerFilter,
    };
};
